import {
  FORMAS_DE_FECHAMENTO,
  MOTIVO_DE_COMANDA,
  PRECO_MAXIMO_DO_PRODUTO,
  QUANTIDADE_MAXIMA_POR_MOVIMENTO,
  ROTULO_SITUACAO_PRODUTO,
  custoDaEntrada,
  custoMedio,
  margemDoProduto,
  margemPorExtenso,
  type CategoriaProduto,
  type ComandaAberta,
  type ComandaFechada,
  type ConsultaDeComandas,
  type EntradaNaLista,
  type FormaDeFechamento,
  type ItemDaEntrada,
  type ProdutoNaTabela,
  type SituacaoProduto,
} from '@arena/shared/schemas';
import { dataValida } from '@arena/shared/tempo';
import { dinheiro, paraCentavos } from '../agenda/formatos.js';
import type { Situacao } from '../ui/primitivos.js';

/**
 * As traduções do bar — o que a tela mostra a partir do que o servidor
 * manda.
 *
 * Nenhuma conta mora aqui. Margem, situação do estoque e reposição
 * sugerida são de `@arena/shared/schemas`, calculadas uma vez e usadas
 * pelos dois lados: o dia em que a tela calcular a margem sozinha é o
 * dia em que ela passa a discordar do servidor por um centavo.
 */

/** A pastilha colorida da coluna Situação de `#v-estoque`. */
export const SITUACAO_NA_TELA: Readonly<Record<SituacaoProduto, Situacao>> = {
  Critico: 'atrasada',
  Baixo: 'atencao',
  Ok: 'ok',
};

export function rotuloDaSituacao(situacao: SituacaoProduto): string {
  return ROTULO_SITUACAO_PRODUTO[situacao];
}

/**
 * A coluna Margem. Traço para quem não recebeu o custo — e é traço
 * mesmo, não "0%": o perfil não está vendo uma margem zero, está sem
 * permissão para vê-la.
 */
export function margemNaTabela(produto: ProdutoNaTabela): string {
  return margemPorExtenso(produto.margem);
}

/**
 * Centavos → o texto que a pessoa digitaria no campo ("7,00"). É o
 * inverso de `paraCentavos`: o que entra preenchido sai igual ao que o
 * servidor tem, e só vira "mudança" se a pessoa mexer.
 */
export function centavosParaCampo(centavos: number): string {
  return (centavos / 100).toFixed(2).replace('.', ',');
}

/**
 * O que a entrada manda do produto além do custo: só o que a pessoa
 * mudou. O servidor aplica preço e mínimo sempre que chegam, então
 * mandar o que a pessoa nem tocou é sobrescrever o produto com o valor
 * que a tela tinha.
 *
 * `produto` é o RETRATO de quando a pessoa escolheu o produto
 * (`RetratoDoProduto`), e não a linha viva da consulta: a consulta se
 * refaz, e outra sessão pode ter reajustado o preço no meio do caminho —
 * o preço que a pessoa nem tocou viraria "mudança" e desfaria o reajuste.
 */
export function camposAlterados(
  produto: Pick<ProdutoNaTabela, 'preco' | 'estoqueMinimo'>,
  form: { preco: number; estoqueMinimo: number },
): { preco?: number; estoqueMinimo?: number } {
  return {
    ...(form.preco === produto.preco ? {} : { preco: form.preco }),
    ...(form.estoqueMinimo === produto.estoqueMinimo ? {} : { estoqueMinimo: form.estoqueMinimo }),
  };
}

/** "aberta às 18:12" — a hora local de quem está olhando a tela. */
export function horaDaAbertura(comanda: ComandaAberta): string {
  return new Date(comanda.abertaEm).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * "Aberta às 18:12" no dia de hoje; "Aberta em 05/03 às 18:12" nos outros —
 * a comanda que o histórico reabre para leitura quase nunca é de hoje, e a
 * hora sozinha engana. Dia e hora no relógio de quem olha (como a hora acima);
 * `agora` entra por parâmetro para o teste.
 */
export function quandoAbriu(comanda: ComandaAberta, agora: Date = new Date()): string {
  const aberta = new Date(comanda.abertaEm);
  const hora = horaDaAbertura(comanda);
  const mesmoDia =
    aberta.getFullYear() === agora.getFullYear() &&
    aberta.getMonth() === agora.getMonth() &&
    aberta.getDate() === agora.getDate();
  if (mesmoDia) return `Aberta às ${hora}`;
  const dia = aberta.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  return `Aberta em ${dia} às ${hora}`;
}

/** O que a lista de itens diz quando está vazia: a cancelada não "ainda" vai receber nada. */
export function textoDeComandaSemItens(
  comanda: Pick<ComandaAberta, 'canceladaEm' | 'fechadaEm'>,
): string {
  return comanda.canceladaEm || comanda.fechadaEm ? 'Nenhum item foi lançado.' : 'Nada lançado ainda.';
}

/** "3 itens" / "1 item" — o contador do modal. */
export function contagemDeItens(comanda: ComandaAberta): string {
  const quantas = comanda.itens.reduce((soma, item) => soma + item.quantidade, 0);
  return `${quantas} ${quantas === 1 ? 'item' : 'itens'}`;
}

/**
 * A comanda ainda aberta de uma lista (a do aluno): nem fechada, nem
 * cancelada. O servidor só admite uma por pessoa, então é a primeira que
 * aparece — e é por ela que a ficha decide entre "Abrir" e "Ver".
 */
export function comandaEmAberto<T extends Pick<ComandaAberta, 'fechadaEm' | 'canceladaEm'>>(
  lista: readonly T[],
): T | undefined {
  return lista.find((comanda) => !comanda.fechadaEm && !comanda.canceladaEm);
}

/**
 * Os limites do motivo de cancelamento e de estorno de comanda — os MESMOS do
 * servidor (`zCancelamentoDeComanda`), porque vêm do mesmo lugar.
 */
export const MOTIVO_DE_CANCELAMENTO = MOTIVO_DE_COMANDA;

/** O motivo digitado serve? Conta o texto sem os espaços das pontas, como o servidor. */
export function motivoValido(texto: string): boolean {
  const tamanho = texto.trim().length;
  return tamanho >= MOTIVO_DE_CANCELAMENTO.minimo && tamanho <= MOTIVO_DE_CANCELAMENTO.maximo;
}

/** As cores do ranking de "Mais vendidos", na ordem do protótipo. */
export const CORES_DO_RANKING = [
  'var(--quadra-2)',
  'var(--quadra-1)',
  'var(--peach)',
  'var(--wine)',
  'var(--panel-3)',
] as const;

// ---------------------------------------------------------------------
// O histórico de comandas
// ---------------------------------------------------------------------

/** O que a URL do histórico guarda: `?aba=historico&de=&ate=&forma=&canceladas=1`. */
export interface FiltrosDoHistorico {
  readonly canceladas: boolean;
  readonly de?: string;
  readonly ate?: string;
  readonly forma?: FormaDeFechamento;
  /** "De" depois de "até": o servidor recusa, então a tela nem pergunta. */
  readonly periodoInvertido: boolean;
}

/**
 * Lê os filtros da URL, ignorando o que não presta: uma data que não existe
 * ou uma forma desconhecida (link velho, texto digitado à mão) vira "sem
 * filtro" em vez de um 400 na cara de quem colou o link.
 *
 * Cancelada não foi paga, então a forma só vale para as fechadas — é a
 * mesma regra do servidor, aplicada aqui para a consulta nunca nascer
 * incoerente.
 */
export function lerFiltrosDoHistorico(params: URLSearchParams): FiltrosDoHistorico {
  const canceladas = params.get('canceladas') === '1';
  const de = dataDaUrl(params.get('de'));
  const ate = dataDaUrl(params.get('ate'));
  const forma = FORMAS_DE_FECHAMENTO.find((f) => f === params.get('forma'));
  return {
    canceladas,
    ...(de ? { de } : {}),
    ...(ate ? { ate } : {}),
    ...(forma && !canceladas ? { forma } : {}),
    periodoInvertido: Boolean(de && ate && de > ate),
  };
}

/** Linhas por página do histórico; "Carregar mais" traz a seguinte. */
export const LIMITE_DO_HISTORICO = 25;

/** O que a consulta do histórico manda ao servidor, a partir dos filtros da URL. */
export function consultaDoHistorico(
  filtros: FiltrosDoHistorico,
  cursor?: string,
): ConsultaDeComandas {
  return {
    situacao: filtros.canceladas ? 'canceladas' : 'fechadas',
    limite: LIMITE_DO_HISTORICO,
    ...(filtros.de ? { de: filtros.de } : {}),
    ...(filtros.ate ? { ate: filtros.ate } : {}),
    ...(filtros.forma ? { forma: filtros.forma } : {}),
    ...(cursor ? { cursor } : {}),
  };
}

function dataDaUrl(texto: string | null): string | undefined {
  return texto && dataValida(texto) ? texto : undefined;
}

/**
 * Só as formas em que entrou valor, na ordem de sempre — a linha de totais
 * sob os filtros não lista "Cartão R$ 0" para quem nunca passou cartão.
 */
export function formasComValor(
  totais: Readonly<Record<FormaDeFechamento, number>>,
): { forma: FormaDeFechamento; valor: number }[] {
  return FORMAS_DE_FECHAMENTO.map((forma) => ({ forma, valor: totais[forma] })).filter(
    (linha) => linha.valor > 0,
  );
}

/** A soma de tudo que o filtro alcançou, em todas as páginas. */
export function somaDosTotais(totais: Readonly<Record<FormaDeFechamento, number>>): number {
  return FORMAS_DE_FECHAMENTO.reduce((soma, forma) => soma + totais[forma], 0);
}

/** "05/03 14:22" — quando a comanda foi fechada (ou cancelada), na hora de quem olha. */
export function quandoEncerrou(comanda: ComandaAberta): string {
  const instante = comanda.fechadaEm ?? comanda.canceladaEm;
  if (!instante) return '—';
  const quando = new Date(instante);
  const dia = quando.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  const hora = quando.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${dia} ${hora}`;
}

/**
 * A forma na coluna do histórico. `ROTULO_FORMA_DE_FECHAMENTO` é o texto do
 * BOTÃO do fechamento ("Lançar na mensalidade"), comprido demais para
 * uma célula; aqui é o nome da forma, e só.
 */
export const FORMA_NO_HISTORICO: Readonly<Record<FormaDeFechamento, string>> = {
  Pix: 'Pix',
  Dinheiro: 'Dinheiro',
  Cartao: 'Cartão',
  Mensalidade: 'Mensalidade',
};

/**
 * Como a comanda terminou, em uma frase — "Fechada em 05/03 14:22 · Pix",
 * "Cancelada em 05/03 14:22 · aberta no nome errado". `null` para a que
 * ainda está aberta.
 */
export function desfechoDaComanda(comanda: ComandaAberta): string | null {
  if (comanda.canceladaEm) {
    const motivo = comanda.motivoCancelamento ? ` · ${comanda.motivoCancelamento}` : '';
    return `Cancelada em ${quandoEncerrou(comanda)}${motivo}`;
  }
  if (comanda.fechadaEm) {
    const forma = comanda.formaPagamento ? ` · ${FORMA_NO_HISTORICO[comanda.formaPagamento]}` : '';
    return `Fechada em ${quandoEncerrou(comanda)}${forma}`;
  }
  return null;
}

/**
 * O toast de quando a comanda fecha. Em "lançar na mensalidade" diz qual das
 * duas coisas aconteceu: o valor entrou na fatura de um mês, ou ficou
 * pendente porque a fatura ainda não nasceu e entra na próxima.
 */
export function avisoDoFechamento(fechada: ComandaFechada): string {
  if (fechada.pendente) {
    return `Comanda fechada. ${dinheiro(fechada.total)} ficam pendentes e entram na próxima fatura que for gerada.`;
  }
  if (fechada.faturaId) {
    const [ano, mes] = (fechada.competencia ?? '').split('-');
    const de = ano && mes ? `${mes}/${ano}` : 'o mês';
    return `Comanda fechada. ${dinheiro(fechada.total)} lançados na fatura da mensalidade de ${de}.`;
  }
  return 'Comanda fechada. Estoque baixado e venda lançada no financeiro.';
}

// ---------------------------------------------------------------------
// A entrada de mercadoria · as linhas do modal
// ---------------------------------------------------------------------

/** Os limites do nome de produto — os mesmos de `zNomeDeProduto` no servidor. */
export const NOME_DO_PRODUTO = { minimo: 2, maximo: 80 } as const;

/** O número da nota fiscal — o mesmo teto de `zEntradaDeMercadoria.nota`. */
export const NOTA_MAXIMA = 40;

/** Quantas linhas uma entrada aceita — o mesmo teto de `zEntradaDeMercadoria.itens`. */
export const LINHAS_MAXIMAS_DA_ENTRADA = 50;

export type ModoDaLinha = 'existente' | 'novo';

/**
 * O que o produto tinha QUANDO a pessoa o escolheu na linha. É contra isto
 * — e não contra a consulta viva — que se mede o que ela mudou (ver
 * `camposAlterados`), e é com isto que se projeta o custo médio.
 */
export interface RetratoDoProduto {
  readonly preco: number;
  readonly estoqueMinimo: number;
  readonly saldo: number;
  /** Ausente para quem não recebeu o custo: a tabela vem inteira com ele ou inteira sem. */
  readonly custo?: number;
}

/**
 * Uma linha do modal de entrada: um produto já cadastrado OU um produto
 * novo. Os campos são o TEXTO que a pessoa digitou — validar e converter é
 * de `itemDaLinha` —, para que um "7," pela metade não vire 0 sem ninguém ver.
 */
export interface LinhaDaEntrada {
  /** A chave do React: estável enquanto a linha existe, ainda que as outras saiam. */
  readonly chave: number;
  readonly modo: ModoDaLinha;
  // produto cadastrado
  readonly produtoId: string;
  readonly retrato: RetratoDoProduto | null;
  // produto novo
  readonly nome: string;
  readonly categoria: CategoriaProduto;
  // os dois
  readonly quantidade: string;
  readonly custo: string;
  readonly preco: string;
  readonly minimo: string;
}

export function linhaVazia(chave: number): LinhaDaEntrada {
  return {
    chave,
    modo: 'existente',
    produtoId: '',
    retrato: null,
    nome: '',
    categoria: 'Bebida',
    quantidade: '',
    custo: '',
    preco: '',
    minimo: '',
  };
}

/**
 * Escolher o produto traz os valores DELE — preço, mínimo e, se o servidor
 * os mandou, o custo — e guarda o retrato. "Escolha…" (`undefined`) esvazia
 * o que tinha vindo do produto e mantém a quantidade.
 *
 * O que a pessoa DIGITOU não se perde: o custo só é trocado quando o
 * produto traz um, e só é esvaziado quando tinha vindo de um.
 */
export function escolherProdutoDaLinha(
  linha: LinhaDaEntrada,
  produto: ProdutoNaTabela | undefined,
): LinhaDaEntrada {
  if (!produto) {
    return {
      ...linha,
      produtoId: '',
      retrato: null,
      preco: '',
      minimo: '',
      custo: linha.retrato?.custo === undefined ? linha.custo : '',
    };
  }
  return {
    ...linha,
    produtoId: produto.id,
    retrato: {
      preco: produto.preco,
      estoqueMinimo: produto.estoqueMinimo,
      saldo: produto.saldo,
      ...(typeof produto.custo === 'number' ? { custo: produto.custo } : {}),
    },
    preco: centavosParaCampo(produto.preco),
    minimo: String(produto.estoqueMinimo),
    custo: typeof produto.custo === 'number' ? centavosParaCampo(produto.custo) : linha.custo,
  };
}

/**
 * Trocar entre "produto cadastrado" e "produto novo" não leva os valores de
 * um para o outro. Clicar no modo que JÁ está ativo devolve a mesma linha —
 * o botão não pode apagar o que a pessoa acabou de preencher.
 */
export function trocarModoDaLinha(linha: LinhaDaEntrada, modo: ModoDaLinha): LinhaDaEntrada {
  if (linha.modo === modo) return linha;
  return { ...escolherProdutoDaLinha(linha, undefined), modo, nome: '', categoria: 'Bebida' };
}

// --- o que cada campo aceita ---------------------------------------------

function inteiroDe(texto: string): number | null {
  const limpo = texto.trim();
  return /^\d+$/.test(limpo) ? Number(limpo) : null;
}

function quantidadeDe(texto: string): number | null {
  const n = inteiroDe(texto);
  return n !== null && n >= 1 && n <= QUANTIDADE_MAXIMA_POR_MOVIMENTO ? n : null;
}

function minimoDe(texto: string): number | null {
  const n = inteiroDe(texto);
  return n !== null && n <= QUANTIDADE_MAXIMA_POR_MOVIMENTO ? n : null;
}

/** Centavos, de zero (o brinde) até o teto do produto. */
function valorDe(texto: string): number | null {
  const centavos = paraCentavos(texto);
  return centavos !== null && centavos <= PRECO_MAXIMO_DO_PRODUTO ? centavos : null;
}

function nomeValido(nome: string): boolean {
  const tamanho = nome.trim().length;
  return tamanho >= NOME_DO_PRODUTO.minimo && tamanho <= NOME_DO_PRODUTO.maximo;
}

/**
 * A linha como o servidor a recebe, ou `null` enquanto estiver incompleta ou
 * fora da faixa. Preço e mínimo de produto cadastrado só viajam se a pessoa
 * os mudou, contra o retrato da escolha; o produto novo leva os dele dentro
 * de `produtoNovo`, e o custo vai só em `custoUnitario`.
 */
export function itemDaLinha(linha: LinhaDaEntrada): ItemDaEntrada | null {
  const quantidade = quantidadeDe(linha.quantidade);
  const custoUnitario = valorDe(linha.custo);
  const preco = valorDe(linha.preco);
  const estoqueMinimo = minimoDe(linha.minimo);
  if (quantidade === null || custoUnitario === null || preco === null || estoqueMinimo === null) {
    return null;
  }

  if (linha.modo === 'novo') {
    if (!nomeValido(linha.nome)) return null;
    return {
      produtoNovo: { nome: linha.nome.trim(), categoria: linha.categoria, preco, estoqueMinimo },
      quantidade,
      custoUnitario,
    };
  }

  if (!linha.produtoId || !linha.retrato) return null;
  return {
    produtoId: linha.produtoId,
    quantidade,
    custoUnitario,
    ...camposAlterados(linha.retrato, { preco, estoqueMinimo }),
  };
}

// --- os avisos de campo --------------------------------------------------

export interface ProblemasDaLinha {
  nome?: string;
  quantidade?: string;
  custo?: string;
  preco?: string;
  minimo?: string;
}

const numeroPorExtenso = (n: number) => new Intl.NumberFormat('pt-BR').format(n);

/**
 * O que dizer sob cada campo. Valor digitado fora da faixa é avisado na hora.
 * Campo em branco, não: faltar é diferente de errar, e um aviso vermelho num
 * campo que a pessoa ainda não alcançou é ruído.
 *
 * A exceção é o MÍNIMO. Ele parece opcional — e o botão ficava desligado
 * sem dizer por quê. Quando é a ÚNICA coisa que falta para a linha ficar
 * pronta, o aviso aparece.
 */
export function problemasDaLinha(linha: LinhaDaEntrada): ProblemasDaLinha {
  const problemas: ProblemasDaLinha = {};

  if (linha.modo === 'novo' && linha.nome.trim() !== '' && !nomeValido(linha.nome)) {
    problemas.nome = `O nome precisa de pelo menos ${NOME_DO_PRODUTO.minimo} letras.`;
  }
  if (linha.quantidade.trim() !== '' && quantidadeDe(linha.quantidade) === null) {
    problemas.quantidade = `Use um número inteiro de 1 a ${numeroPorExtenso(QUANTIDADE_MAXIMA_POR_MOVIMENTO)}.`;
  }
  const faixaDeValor = `Informe um valor entre ${dinheiro(0)} e ${dinheiro(PRECO_MAXIMO_DO_PRODUTO)}, como 3,20.`;
  if (linha.custo.trim() !== '' && valorDe(linha.custo) === null) problemas.custo = faixaDeValor;
  if (linha.preco.trim() !== '' && valorDe(linha.preco) === null) problemas.preco = faixaDeValor;

  if (linha.minimo.trim() !== '') {
    if (minimoDe(linha.minimo) === null) {
      problemas.minimo = `Use um número inteiro de 0 a ${numeroPorExtenso(QUANTIDADE_MAXIMA_POR_MOVIMENTO)}.`;
    }
  } else if (itemDaLinha({ ...linha, minimo: '0' }) !== null) {
    problemas.minimo = 'Informe o estoque mínimo.';
  }
  return problemas;
}

// --- o total e a margem --------------------------------------------------

/** Quanto a linha custa: quantidade × custo, ou zero enquanto faltar um dos dois. */
export function totalDaLinha(linha: LinhaDaEntrada): number {
  const quantidade = quantidadeDe(linha.quantidade);
  const custoUnitario = valorDe(linha.custo);
  return quantidade !== null && custoUnitario !== null
    ? custoDaEntrada(quantidade, custoUnitario)
    : 0;
}

/** A saída que a entrada vai gerar no financeiro: a soma das linhas que já têm quantidade e custo. */
export function totalDaEntrada(linhas: readonly LinhaDaEntrada[]): number {
  return linhas.reduce((soma, linha) => soma + totalDaLinha(linha), 0);
}

export interface MargemDaLinha {
  /** Centésimos de ponto; `null` quando o preço é zero (a regra do shared). */
  readonly margem: number | null;
  /** Sobre o quê a margem foi calculada. */
  readonly base: 'custo médio' | 'custo da compra';
  /** O custo da base, em centavos. */
  readonly custo: number;
}

/**
 * A margem que a linha vai ter DEPOIS da entrada.
 *
 * Produto cadastrado: sobre o CUSTO MÉDIO projetado — `custoMedio` de
 * `@arena/shared`, a mesma conta que o servidor faz, sobre o saldo e o custo
 * do retrato. O custo digitado é o da compra; o do produto, depois dela, é o
 * médio, e é nele que a margem de verdade se apoia. Produto novo: o médio é
 * a própria compra.
 *
 * `null` quando não há o que mostrar: sem permissão de custo (`veOCusto`
 * falso — nem a conta que a pessoa poderia fazer com o que digitou), ou
 * enquanto faltar quantidade, custo ou preço.
 */
export function margemDaLinha(
  linha: LinhaDaEntrada,
  veOCustoNaTela: boolean,
): MargemDaLinha | null {
  if (!veOCustoNaTela) return null;
  const quantidade = quantidadeDe(linha.quantidade);
  const custoUnitario = valorDe(linha.custo);
  const preco = valorDe(linha.preco);
  if (quantidade === null || custoUnitario === null || preco === null) return null;

  if (linha.modo === 'novo') {
    return {
      margem: margemDoProduto(custoUnitario, preco),
      base: 'custo da compra',
      custo: custoUnitario,
    };
  }

  const custoAtual = linha.retrato?.custo;
  if (!linha.retrato || custoAtual === undefined) return null;
  const medio = custoMedio(linha.retrato.saldo, custoAtual, quantidade, custoUnitario);
  return { margem: margemDoProduto(medio, preco), base: 'custo médio', custo: medio };
}

/**
 * Quem vê o custo. O servidor só o manda a quem pode vê-lo, e a tabela vem
 * inteira com ele ou inteira sem — por isso o dado decide. Catálogo vazio
 * não tem tabela para ler (a primeira entrada de uma arena nova), e então a
 * projeção da sessão desempata. É cosmético, como toda checagem do front: o
 * custo que chega à tela é só o que o servidor mandou.
 */
export function veOCusto(produtos: readonly ProdutoNaTabela[], projecao?: string): boolean {
  if (produtos.length > 0) return produtos.some((p) => typeof p.custo === 'number');
  return projecao === 'completo';
}

// ---------------------------------------------------------------------
// As abas do estoque
// ---------------------------------------------------------------------

/**
 * As abas de `#v-estoque`, em ordem. Mais uma aba é mais uma linha aqui e
 * o conteúdo dela em `PaginaDoEstoque`; a leitura da URL não muda.
 */
export const ABAS_DO_ESTOQUE = [
  { valor: 'produtos', rotulo: 'Produtos' },
  { valor: 'entradas', rotulo: 'Entradas' },
  { valor: 'inventarios', rotulo: 'Inventários' },
] as const;

export type AbaDoEstoque = (typeof ABAS_DO_ESTOQUE)[number]['valor'];

/** A aba da URL (`?aba=entradas`); sem ela, ou com uma que não existe, a primeira. */
export function lerAbaDoEstoque(params: URLSearchParams): AbaDoEstoque {
  const pedida = params.get('aba');
  return ABAS_DO_ESTOQUE.find((aba) => aba.valor === pedida)?.valor ?? ABAS_DO_ESTOQUE[0].valor;
}

// ---------------------------------------------------------------------
// O histórico de entradas
// ---------------------------------------------------------------------

/**
 * A pastilha da coluna Situação: o que o lançamento da saída diz. Estornada
 * vem antes de tudo — a saída continua "realizada" no razão, mas a compra
 * deixou de valer.
 */
export function situacaoDaEntrada(
  entrada: Pick<EntradaNaLista, 'situacaoFinanceira' | 'estornadaEm'>,
): { situacao: Situacao; rotulo: string } {
  if (entrada.estornadaEm) return { situacao: 'neutra', rotulo: 'Estornada' };
  if (entrada.situacaoFinanceira === 'Realizado') return { situacao: 'ok', rotulo: 'Realizado' };
  if (entrada.situacaoFinanceira === 'Previsto') {
    return { situacao: 'aguardando', rotulo: 'Previsto' };
  }
  return { situacao: 'neutra', rotulo: 'Sem lançamento' };
}

/**
 * Os itens de uma entrada para a célula da tabela: "10 × Coca-Cola 350 ml",
 * com o custo unitário quando ele veio, e quantos ficaram de fora do limite.
 */
export function resumoDosItens(
  itens: EntradaNaLista['itens'],
  limite = 3,
): { linhas: string[]; restantes: number } {
  return {
    linhas: itens
      .slice(0, limite)
      .map(
        (item) =>
          `${item.quantidade} × ${item.produto.nome}` +
          (typeof item.custoUnitario === 'number' ? ` · ${dinheiro(item.custoUnitario)}` : ''),
      ),
    restantes: Math.max(itens.length - limite, 0),
  };
}

/** "05/03" — o dia em que a entrada foi estornada, na hora de quem olha. */
export function diaDoEstorno(estornadaEm: string): string {
  return new Date(estornadaEm).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

/** As colunas de custo só existem quando o servidor mandou o custo de alguma entrada. */
export function entradaComCusto(entradas: readonly Pick<EntradaNaLista, 'custoTotal'>[]): boolean {
  return entradas.some((entrada) => typeof entrada.custoTotal === 'number');
}

/** "05/03/26 14:22" — quando a entrada foi registrada, na hora de quem olha. */
export function quandoEntrou(criadaEm: string): string {
  const quando = new Date(criadaEm);
  const dia = quando.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
  const hora = quando.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${dia} ${hora}`;
}
